/*
 * Where a keystroke becomes an action. One listener on the document, one registry, and a stack of
 * scopes that surfaces push while they are open: the composer pushes "composer", the message list
 * pushes "messageList", and a binding fires only while its scope is on the stack.
 *
 * The listener is on the document rather than on a container so that a binding still works when
 * focus sits on the page body — and it takes the event only when the registry answers, so nothing
 * that is not a shortcut is ever swallowed.
 *
 * It is also where a binding speaks. A chord that moves the scroll position, the read cursor or a
 * divider changes nothing a screen reader notices, so a binding may name an `announceKey`
 * (`shortcuts.ts`) and this listener says it through the one announcer once the action has run. One
 * line per binding, and the whole keyboard becomes audible (X-B3 A2).
 */

import {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	type ReactNode,
} from "react"
import { readJSON, storageKey } from "../../lib/storage.ts"
import type { ShortcutDef } from "../modules/registry.ts"
import { useAnnounce } from "./Announcer.tsx"
import {
	createShortcutRegistry,
	isTextEntry,
	runShortcut,
	type ShortcutRegistry,
	type ShortcutScope,
} from "./shortcuts.ts"

interface ShortcutContextValue {
	registry: ShortcutRegistry
	push: (scope: ShortcutScope) => void
	pop: (scope: ShortcutScope) => void
}

const ShortcutContext = createContext<ShortcutContextValue | null>(null)

export function ShortcutProvider({
	defs,
	overrides: fromAccount,
	children,
}: {
	defs: ShortcutDef[]
	/**
	 * The account's own remapping — the registered setting `core.shell.shortcut_overrides`, handed
	 * in by the shell (G-51). It wins over the device's copy: a key somebody chose belongs to them
	 * and not to the machine they chose it on, and the device copy is what a boot whose settings map
	 * has not arrived yet still has to go on. No screen writes either of them this round.
	 */
	overrides?: Record<string, string[]> | undefined
	children: ReactNode
}) {
	// The device's remapping is read once per boot: re-reading storage on every keystroke would put
	// a synchronous read in the input path.
	const onThisDevice = useMemo(
		() => readJSON<Record<string, string[]>>(storageKey("shortcuts")) ?? {},
		[],
	)
	const overrides = useMemo(
		() => ({ ...onThisDevice, ...fromAccount }),
		[onThisDevice, fromAccount],
	)
	const registry = useMemo(() => createShortcutRegistry(defs, overrides), [defs, overrides])
	const scopes = useRef<ShortcutScope[]>(["global"])
	const announce = useAnnounce()

	const value = useMemo<ShortcutContextValue>(
		() => ({
			registry,
			push: (scope) => scopes.current.push(scope),
			pop: (scope) => {
				const index = scopes.current.lastIndexOf(scope)
				if (index > 0) scopes.current.splice(index, 1)
			},
		}),
		[registry],
	)

	useEffect(() => {
		function onKeyDown(event: KeyboardEvent) {
			// An IME composing a character sends keydowns of its own; none of them is a shortcut.
			if (event.isComposing || event.defaultPrevented) return
			const found = registry.find(event, scopes.current, isTextEntry(event.target))
			if (!found) return
			event.preventDefault()
			// And the binding says what it did, where what it did is invisible (`announceKey`): this is
			// the one place every chord of the application passes through, so it is the one place that
			// can speak for all of them.
			runShortcut(found, (key) => announce(key))
		}
		document.addEventListener("keydown", onKeyDown)
		return () => document.removeEventListener("keydown", onKeyDown)
	}, [registry, announce])

	return <ShortcutContext.Provider value={value}>{children}</ShortcutContext.Provider>
}

function useShortcutContext(): ShortcutContextValue {
	const value = useContext(ShortcutContext)
	if (!value) throw new Error("shortcut hook outside ShortcutProvider")
	return value
}

/** The registry itself — the sheet, a menu hint and a tooltip all render out of it. */
export function useShortcutRegistry(): ShortcutRegistry {
	return useShortcutContext().registry
}

/**
 * Marks a scope as open for as long as the calling component is mounted. A surface calls it once;
 * nesting is a stack, so a dialog above the message list can push its own scope and take it back.
 */
export function useShortcutScope(scope: ShortcutScope): void {
	const { push, pop } = useShortcutContext()
	useEffect(() => {
		push(scope)
		return () => pop(scope)
	}, [push, pop, scope])
}

/**
 * Opens the keyboard sheet the way a key does: by running the binding that documents it. The sheet
 * is the shell's own state and the shell registers `sheet` for it, so this is the one handle on it
 * that does not ask `Shell.tsx` to thread a callback through every control that wants one — the
 * account menu's row and the quick switcher's both use it.
 */
export function useOpenShortcutSheet(): () => void {
	const registry = useShortcutRegistry()
	return useCallback(() => {
		registry
			.all()
			.find((def) => def.id === "sheet")
			?.run()
	}, [registry])
}
